import { useCallback, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import { normalizeEmail } from '../utils/editBookingNormalize'
import type { EditBookingFormValues } from '../utils/editBookingValidation'

import type { FindDriverControllerData } from './useFindDriverController'
import { useFindDriverController } from './useFindDriverController'

type UseFindControllerFlowArgs = {
  form: UseFormReturn<EditBookingFormValues>
}

export type UseFindControllerFlowResult = {
  pendingControllerData: FindDriverControllerData | null
  handleControllerEmailBlur: (email: string) => void
  onConfirmController: () => void
  onCancelController: () => void
  isFindControllerPending: boolean
}

export const useFindControllerFlow = ({
  form,
}: UseFindControllerFlowArgs): UseFindControllerFlowResult => {
  const [pendingControllerData, setPendingControllerData] =
    useState<FindDriverControllerData | null>(null)
  const findDriverControllerMutation = useFindDriverController()

  const handleControllerEmailBlur = useCallback(
    (email: string) => {
      if (form.getValues('vehicleControllerId') != null) return
      const normalizedEmail = normalizeEmail(email)
      // 事前ガード: API を叩く前にメール形式のみ簡易判定。本検証は yup スキーマで実施
      if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) return
      if (findDriverControllerMutation.isPending) return

      findDriverControllerMutation.mutate(
        { vehicleControllerEmail: normalizedEmail },
        {
          onSuccess: (data) => {
            if (data) setPendingControllerData(data)
          },
        }
      )
    },
    [findDriverControllerMutation, form]
  )

  const onConfirmController = useCallback(() => {
    if (!pendingControllerData) return
    form.setValue('vehicleControllerId', pendingControllerData.vehicleControllerId, {
      shouldDirty: true,
    })
    form.setValue('companyId', pendingControllerData.companyId, { shouldDirty: true })
    form.setValue('companyName', pendingControllerData.companyName ?? '', { shouldDirty: true })
    form.setValue('vehicleControllerName', pendingControllerData.vehicleControllerName ?? '', {
      shouldDirty: true,
    })
    form.setValue('vehicleControllerPhone', pendingControllerData.vehicleControllerPhone ?? '', {
      shouldDirty: true,
    })
    setPendingControllerData(null)
  }, [pendingControllerData, form])

  const onCancelController = useCallback(() => {
    form.setValue('vehicleControllerEmail', '', { shouldDirty: true })
    setPendingControllerData(null)
  }, [form])

  return {
    pendingControllerData,
    handleControllerEmailBlur,
    onConfirmController,
    onCancelController,
    isFindControllerPending: findDriverControllerMutation.isPending,
  }
}
